import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader } from "lucide-react";
import { fetchCreditSession } from "@/server/services/creditsService";
import { fetchCreditBalance } from "@/server/services/stripeService";
import { ActiveNavMenu, useNavigationState } from "@/zustand/NavigationState";
import { QUERY_KEYS } from "@/config/queryKeys";
import SuccessView from "@/components/chat/middle-column/credits/SuccessView";
import { safeSession } from "@/utils/safeStorage";
import type {
  PurchaseResult,
  PurchaseError,
} from "@/components/chat/middle-column/credits/creditsConfig";

export default function CreditsSuccessPage() {
  const navigate = useNavigate();
  const cancelPaymentsView = useNavigationState((s) => s.cancelPaymentsView);

  // Read once on mount; cleared immediately so a page refresh doesn't reuse it.
  const [prePurchaseBalance] = useState(() => {
    const stored = safeSession.get("credits_pre_purchase_balance");
    safeSession.remove("credits_pre_purchase_balance");
    const parsed = Number(stored);
    return stored !== null && !isNaN(parsed) ? parsed : 0;
  });
  const setActiveNavMenu = useNavigationState((s) => s.setActiveNavMenu);
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");

  const [purchaseResult, setPurchaseResult] = useState<PurchaseResult | null>(
    null,
  );
  const [purchaseError, setPurchaseError] = useState<PurchaseError | null>(
    null,
  );

  // Guard: no session_id → not a valid checkout return
  useEffect(() => {
    if (!sessionId) {
      navigate("/", { replace: true });
    }
  }, [sessionId, navigate]);

  // Poll every 3s until the post-purchase balance lands (webhook is async).
  // Stops automatically once availableCredits exceeds the pre-purchase snapshot.
  const { data: balanceData } = useQuery({
    queryKey: QUERY_KEYS.stripeCreditBalance,
    queryFn: fetchCreditBalance,
    staleTime: 30_000,
    enabled: !!sessionId && !purchaseError,
    refetchInterval: (query) =>
      Math.floor(query.state.data?.availableCredits ?? 0) > prePurchaseBalance
        ? false
        : 3_000,
  });

  useEffect(() => {
    if (!sessionId) {
      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        const session = await fetchCreditSession(sessionId);
        if (cancelled) {
          return;
        }

        if (session.paymentStatus !== "paid") {
          setPurchaseError({ sessionId });
          return;
        }

        setPurchaseResult({
          amount: session.credits,
          dollars: session.amountUsd,
          newBalance: 0, // overridden at render from live balanceData
          sessionId,
        });
      } catch {
        if (!cancelled) {
          setPurchaseError({ sessionId });
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const handleBackToChat = () => {
    cancelPaymentsView();
    setActiveNavMenu(ActiveNavMenu.AI_CHAT);
    navigate("/", { replace: true });
  };
  const handleRetry = () => navigate("/credits", { replace: true });

  if (!sessionId) {
    return null;
  }

  // Still loading session details
  if (!purchaseResult && !purchaseError) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-background text-foreground">
        <div className="flex flex-col items-center gap-3 rounded-md bg-card p-8">
          <Loader className="size-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  const liveBalance = Math.floor(balanceData?.availableCredits ?? 0);

  return (
    <div className="flex h-screen w-full items-center justify-center bg-background text-foreground">
      <div className="w-full max-w-[520px] px-4">
        <SuccessView
          result={
            purchaseResult
              ? { ...purchaseResult, newBalance: liveBalance }
              : null
          }
          error={purchaseError}
          onBackToChat={handleBackToChat}
          onRetry={handleRetry}
        />
      </div>
    </div>
  );
}
